import { useEffect, useRef, type ReactNode } from 'react'
import { DrawerDialog } from '@cannect/new-components/molecules'

type DialogShellProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
}

// Diálogos abertos por estado (sem Trigger) não devolvem o foco sozinhos:
// guarda quem tinha o foco ao abrir e volta para ele ao fechar.
const useRestoreFocus = (open: boolean) => {
  const previous = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (open) {
      previous.current = document.activeElement as HTMLElement | null
      return undefined
    }
    const target = previous.current
    previous.current = null
    if (!target || !target.isConnected) return undefined
    const timer = setTimeout(() => target.focus({ preventScroll: true }), 0)
    return () => clearTimeout(timer)
  }, [open])
}

// Todos os diálogos da área: Dialog no desktop, painel inferior no celular.
export const DialogShell = ({ open, onOpenChange, title, description, children, footer }: DialogShellProps) => {
  useRestoreFocus(open)

  return (
    <DrawerDialog.Root open={open} onOpenChange={onOpenChange}>
      <DrawerDialog.Content className="max-h-[90vh] gap-4 overflow-y-auto px-4 pb-4 md:max-w-lg md:px-6 md:pb-6">
        <DrawerDialog.Header className="text-left">
          <DrawerDialog.Title>{title}</DrawerDialog.Title>
          {description && (
            <DrawerDialog.Description className="text-sm text-neutral-700">{description}</DrawerDialog.Description>
          )}
        </DrawerDialog.Header>
        {children}
        {footer && <DrawerDialog.Footer className="gap-2 sm:gap-0">{footer}</DrawerDialog.Footer>}
      </DrawerDialog.Content>
    </DrawerDialog.Root>
  )
}
